/**
 * The hero at the top of a Contentful product-category page (`/<slug>`, for example `/aluminum`):
 * the category title, a "Help me find the right metal" button, the intro blurb, the category's
 * product list, its General Reference PDFs, and an optional second blurb.
 *
 * Rendered only by `components/Contentful/Product.tsx` → `ProductPage`, which fills
 * `staticContent` from `content/ryerson/products/productStaticContent.ts` → `StaticHeroContent`
 * and `dynamicContent` from the `productCategoryPage` entry. From the `lg` breakpoint up it shows
 * `./Hero/HeroDesktop.tsx`, below it `./Hero/HeroMobile.tsx`. The sub-category pages have their
 * own hero in `./SubcategoryHero/`.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import ProductsHeroDesktop from "./Hero/HeroDesktop";
import ProductsHeroMobile from "./Hero/HeroMobile";
import { ContentfulProduct, ContentfulGeneralReference } from "./Types/Types";

/** One language's fixed hero labels (`productStaticContent.ts` → `StaticHeroContent`). */
export type HeroStaticContent = {
	buttonLabel: string;
	/** The hero button's target, `/store` in every language. Rendered as a plain anchor. */
	buttonActionUrl: string;
	/** Heading over the product list. */
	productsLabel: string;
	/** Heading over the General Reference PDFs. */
	referenceTitle: string;
};

/** The hero fields `Product.tsx` takes from the `productCategoryPage` entry. */
export type HeroDynamicContent = {
	title: string;
	blurb: string;
	/**
	 * Optional second blurb in the hero: its last block on desktop, between the product list and
	 * the General Reference PDFs on mobile. Desktop leaves it out when empty; mobile renders it
	 * as is.
	 */
	blurb2?: string | null;
	/** The entry's `products`; each links to its `linkToProduct`. Required by both layouts. */
	products: ContentfulProduct[];
	/** The entry's `generalReferences` PDFs. Required by both layouts. */
	references: ContentfulGeneralReference[];
};

/** Props shared by the hero and its desktop and mobile layouts. */
export type ProductHeroProps = {
	staticContent: HeroStaticContent;
	dynamicContent: HeroDynamicContent;
};

/** Picks the desktop or mobile category hero by breakpoint; see the file header. */
const ProductsHero: React.FC<ProductHeroProps> = ({ staticContent, dynamicContent }) => {
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ProductsHeroDesktop
					staticContent={staticContent}
					dynamicContent={dynamicContent}
				/>
			</Media>
			<Media lessThan="lg">
				<ProductsHeroMobile staticContent={staticContent} dynamicContent={dynamicContent} />
			</Media>
		</>
	);
};

export default ProductsHero;
